/**
 * Configurações da aplicação com validações de segurança
 */

import { validateApiUrl } from "./lib/security/validators/url.validator";
import {
	type FrontendEnvironmentConfig,
	FrontendConfigError,
	getSocketConfigurationError,
	resolveFrontendEnvironmentConfig,
	resolveFrontendUrl,
} from "./config/runtimeEnv";

let loggerModule: typeof import("./lib/terminalLogger") | null = null;
const getLogger = () => {
	if (!loggerModule) {
		try {
			loggerModule = require("./lib/terminalLogger");
		} catch {
			return null;
		}
	}
	return loggerModule?.logger;
};

const getEnvironment = (): "development" | "production" | "test" => {
	const env =
		import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";
	if (!["development", "production", "test"].includes(env)) {
		const logger = getLogger();
		if (logger) {
			logger.warn(
				`Ambiente inválido: ${env}. Usando 'development' como fallback.`,
			);
		} else {
			console.warn(
				`Ambiente inválido: ${env}. Usando 'development' como fallback.`,
			);
		}
		return "development";
	}
	return env as "development" | "production" | "test";
};

const validatePositiveNumber = (
	value: string | undefined,
	defaultValue: number,
	name: string,
): number => {
	const num = Number(value ?? defaultValue);
	if (isNaN(num) || num <= 0) {
		const logger = getLogger();
		if (logger) {
			logger.warn(
				`Valor inválido para ${name}: ${value}. Usando valor padrão: ${defaultValue}`,
			);
		} else {
			console.warn(
				`Valor inválido para ${name}: ${value}. Usando valor padrão: ${defaultValue}`,
			);
		}
		return defaultValue;
	}
	return num;
};

export const ENVIRONMENT = getEnvironment();
export const IS_PRODUCTION = ENVIRONMENT === "production";
export const IS_DEVELOPMENT = ENVIRONMENT === "development";
export const FRONTEND_ENV_CONFIG: FrontendEnvironmentConfig =
	resolveFrontendEnvironmentConfig(
		import.meta.env as Record<string, string | undefined>,
	);
export const USE_PROXY = FRONTEND_ENV_CONFIG.useProxy;

export const API_URL = validateApiUrl(FRONTEND_ENV_CONFIG.apiUrl);

const getQuotationApiUrl = (): string => {
	const envUrl = import.meta.env.VITE_QUOTATION_API_URL;

	// Em desenvolvimento, o fluxo de orçamento deve usar o backend unificado.
	// Mantemos VITE_QUOTATION_API_URL apenas como compatibilidade legada fora do dev.
	if (IS_DEVELOPMENT) {
		return API_URL;
	}

	if (envUrl !== undefined && envUrl !== null) {
		const trimmed = String(envUrl).trim();

		if (trimmed === "") {
			return "";
		}

		if (trimmed !== "") {
			return validateApiUrl(trimmed);
		}
	}

	return API_URL;
};

export const QUOTATION_API_URL = getQuotationApiUrl();

if (IS_DEVELOPMENT && typeof window !== "undefined") {
	console.log("[CONFIG] API_URL:", API_URL);
	console.log("[CONFIG] Fonte: VITE_API_URL =", FRONTEND_ENV_CONFIG.apiUrl);
	console.log(
		"[CONFIG] QUOTATION_API_URL (Ferramenta Calcular Orçamento):",
		QUOTATION_API_URL || "(relativa - usando proxy)",
	);
	console.log(
		"[CONFIG] Fonte: VITE_QUOTATION_API_URL =",
		import.meta.env.VITE_QUOTATION_API_URL || "(não definida, usando API_URL)",
	);
}

const getFrontendUrl = (): string => {
	return resolveFrontendUrl(
		FRONTEND_ENV_CONFIG,
		typeof window !== "undefined" && window.location
			? window.location.origin
			: undefined,
	);
};

let _frontendUrl: string = getFrontendUrl();

if (typeof window !== "undefined" && window.location) {
	_frontendUrl = window.location.origin;

	if (IS_DEVELOPMENT) {
		const logger = getLogger();
		if (logger) {
			logger.info("FRONTEND_URL detectada dinamicamente:", {
				hostname: window.location.hostname,
				port: window.location.port || "default",
				frontendUrl: _frontendUrl,
				isLocalhost:
					window.location.hostname === "localhost" ||
					window.location.hostname === "127.0.0.1",
			});
		} else {
			console.log(
				"🔗 FRONTEND_URL detectada:",
				_frontendUrl,
				"(hostname:",
				window.location.hostname + ")",
			);
		}
	}
}

export const FRONTEND_URL = _frontendUrl;

function validateSocketApiUrl(): { url: string | null; error: string | null } {
	if (USE_PROXY) {
		return { url: null, error: null };
	}

	const configuredWsUrl = FRONTEND_ENV_CONFIG.wsApiUrl;
	if (!configuredWsUrl) {
		return {
			url: null,
			error:
				getSocketConfigurationError(FRONTEND_ENV_CONFIG) ||
				"Configuração de WebSocket ausente.",
		};
	}

	try {
		return {
			url: validateApiUrl(configuredWsUrl, {
				allowedProtocols: ["http:", "https:", "ws:", "wss:"],
			}),
			error: null,
		};
	} catch (error) {
		return {
			url: null,
			error:
				error instanceof Error
					? `VITE_WS_API_URL inválida: ${error.message}`
					: "VITE_WS_API_URL inválida.",
		};
	}
}

const validatedSocketUrl = validateSocketApiUrl();
export const SOCKET_API_URL = validatedSocketUrl.url;
export const SOCKET_CONFIGURATION_ERROR = validatedSocketUrl.error;
export const DEBUG_INGEST_URL = FRONTEND_ENV_CONFIG.debugIngestUrl || null;

export function getFrontendUrlDynamic(): string {
	return getFrontendUrl();
}

function normalizeHealthcheckPath(pathValue?: string): string {
	const rawPath = String(pathValue || "/health").trim();
	if (!rawPath) {
		return "/health";
	}

	const normalizedPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

	try {
		const apiUrl = new URL(API_URL);
		const apiBasePath = apiUrl.pathname.replace(/\/+$/, "");

		if (
			apiBasePath &&
			apiBasePath !== "/" &&
			normalizedPath.startsWith(`${apiBasePath}/`)
		) {
			return normalizedPath.slice(apiBasePath.length) || "/health";
		}
	} catch {
		// Mantém o path original normalizado
	}

	return normalizedPath;
}

export const HEALTHCHECK_PATH = normalizeHealthcheckPath(
	import.meta.env.VITE_HEALTHCHECK_PATH,
);
export const REQUEST_TIMEOUT_MS = validatePositiveNumber(
	import.meta.env.VITE_REQUEST_TIMEOUT_MS,
	15000,
	"REQUEST_TIMEOUT_MS",
);

export const OPEN_FILE_ACK_TIMEOUT_MS = validatePositiveNumber(
	import.meta.env.VITE_OPEN_FILE_ACK_TIMEOUT_MS,
	45000,
	"OPEN_FILE_ACK_TIMEOUT_MS",
);
export const SAVE_FILE_RESPONSE_TIMEOUT_MS = validatePositiveNumber(
	import.meta.env.VITE_SAVE_FILE_RESPONSE_TIMEOUT_MS,
	900000,
	"SAVE_FILE_RESPONSE_TIMEOUT_MS",
);

export const COREL_PROGRESS_SILENCE_TIMEOUT_MS = validatePositiveNumber(
	import.meta.env.VITE_COREL_PROGRESS_SILENCE_TIMEOUT_MS,
	420000,
	"COREL_PROGRESS_SILENCE_TIMEOUT_MS",
);

export const ENABLE_DEBUG_LOGS =
	IS_DEVELOPMENT && import.meta.env.VITE_ENABLE_DEBUG_LOGS !== "false";
export const CSP_REPORT_URI = import.meta.env.VITE_CSP_REPORT_URI || "";
export const SECURITY_HEADERS_ENABLED =
	import.meta.env.VITE_SECURITY_HEADERS_ENABLED !== "false";

export function getRuntimeSocketServerUrl(): string | null {
	const configError = getSocketConfigurationError(
		FRONTEND_ENV_CONFIG,
		typeof window !== "undefined" && window.location
			? window.location.origin
			: undefined,
	);

	if (configError) {
		return null;
	}

	if (USE_PROXY) {
		return typeof window !== "undefined" && window.location
			? window.location.origin
			: null;
	}

	return SOCKET_API_URL;
}

export function getFrontendUrlForNonBrowser(): string {
	try {
		return resolveFrontendUrl(FRONTEND_ENV_CONFIG);
	} catch (error) {
		if (error instanceof FrontendConfigError) {
			throw error;
		}

		throw new FrontendConfigError("Falha ao resolver FRONTEND_URL.");
	}
}

export const RATE_LIMIT_CONFIG = {
	login: {
		max: validatePositiveNumber(
			import.meta.env.VITE_RATE_LIMIT_LOGIN_MAX,
			5,
			"RATE_LIMIT_LOGIN_MAX",
		),
		windowMs: validatePositiveNumber(
			import.meta.env.VITE_RATE_LIMIT_LOGIN_WINDOW_MS,
			900000,
			"RATE_LIMIT_LOGIN_WINDOW_MS",
		),
	},
	api: {
		max: validatePositiveNumber(
			import.meta.env.VITE_RATE_LIMIT_API_MAX,
			100,
			"RATE_LIMIT_API_MAX",
		),
		windowMs: validatePositiveNumber(
			import.meta.env.VITE_RATE_LIMIT_API_WINDOW_MS,
			900000,
			"RATE_LIMIT_API_WINDOW_MS",
		),
	},
};

export const APP_VERSION = "1.0.0";

export const getSafeErrorMessage = (
	error: string | Error | unknown,
	genericMessage: string = "Ocorreu um erro. Tente novamente.",
): string => {
	if (IS_DEVELOPMENT) {
		if (error instanceof Error) {
			return error.message;
		}
		if (typeof error === "string") {
			return error;
		}
		return String(error);
	}

	return genericMessage;
};

if (IS_PRODUCTION) {
	if (!API_URL.startsWith("https://")) {
		const logger = getLogger();
		if (logger) {
			logger.error(
				"ERRO DE SEGURANÇA: API_URL deve usar HTTPS em produção",
				undefined,
				true,
			);
		} else {
			console.error("ERRO DE SEGURANÇA: API_URL deve usar HTTPS em produção");
		}
	}

	if (ENABLE_DEBUG_LOGS) {
		const logger = getLogger();
		if (logger) {
			logger.warn(
				"AVISO DE SEGURANÇA: Debug logs estão habilitados em produção",
			);
		} else {
			console.warn(
				"AVISO DE SEGURANÇA: Debug logs estão habilitados em produção",
			);
		}
	}

	const logger = getLogger();
	if (logger) {
		logger.info("Aplicação iniciada em modo produção", {
			version: APP_VERSION,
			apiUrl: API_URL.replace(/\/\/[^/]+/, "//[REDACTED]"),
			environment: ENVIRONMENT,
		});
	} else {
		console.log("Aplicação iniciada em modo produção", {
			version: APP_VERSION,
			apiUrl: API_URL.replace(/\/\/[^/]+/, "//[REDACTED]"),
			environment: ENVIRONMENT,
		});
	}
} else {
	const logger = getLogger();
	if (logger) {
		logger.info("Configuração da aplicação:", {
			environment: ENVIRONMENT,
			apiUrl: API_URL,
			debugLogs: ENABLE_DEBUG_LOGS,
			version: APP_VERSION,
			useProxy: USE_PROXY,
			socketConfigured: !SOCKET_CONFIGURATION_ERROR,
		});
	} else {
		console.log("Configuração da aplicação:", {
			environment: ENVIRONMENT,
			apiUrl: API_URL,
			debugLogs: ENABLE_DEBUG_LOGS,
			version: APP_VERSION,
			useProxy: USE_PROXY,
			socketConfigured: !SOCKET_CONFIGURATION_ERROR,
		});
	}
}
